import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button, Card } from "../../../components";
import { Input } from "../../../components/input";
import { SelectV2 } from "../../../components/select-v2";
import { useAuthSession } from "../../auth/shared/hooks/auth-session-context";
import { useResidentReportFilterOptions } from "../resident/hooks/useResidentReportFilterOptions";

type ReportFormat = "pdf" | "powerpoint" | "csv";

type ReportTemplate = {
  id: string;
  title: string;
  description: string;
  audience: string;
  access: "aggregate" | "individual";
  route: string;
};

const reportTemplates: ReportTemplate[] = [
  {
    id: "nri-impact",
    title: "NRI Impact Report",
    description: "Resident-level evidence across NRI priority neighborhoods and impact goals.",
    audience: "City leadership, NRI partners, and funders",
    access: "aggregate",
    route: "/reports/nri-impact",
  },
  {
    id: "resident-report",
    title: "Resident Report",
    description: "A resident profile with engagement, participation, and next-best-action signals.",
    audience: "Program staff and case managers",
    access: "individual",
    route: "/reports/residents",
  },
  {
    id: "program-performance",
    title: "Program Portfolio",
    description:
      "Attendance, retention, satisfaction, and the next best program step across the portfolio.",
    audience: "Department leaders and program managers",
    access: "aggregate",
    route: "/reports/program-performance",
  },
  {
    id: "equity-reach",
    title: "Neighborhoods",
    description: "Participation gaps and targeted actions across the eight NRI focus areas.",
    audience: "Office of Equity and Council",
    access: "aggregate",
    route: "/reports/equity-reach",
  },
];

export function ReportsPresentationSample() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthSession();
  const [selectedTemplateId, setSelectedTemplateId] = useState(reportTemplates[0].id);
  const [scope, setScope] = useState("");
  const [period, setPeriod] = useState("last_quarter");
  const [comparison, setComparison] = useState("last_year");
  const [format, setFormat] = useState<ReportFormat>("pdf");
  const [cadence, setCadence] = useState("monthly");
  const [recipients, setRecipients] = useState("");
  const selectedTemplate =
    reportTemplates.find((template) => template.id === selectedTemplateId) ?? reportTemplates[0];
  const {
    populationOptions: neighborhoodOptions,
    isLoadingOrganizations: isLoadingNeighborhoods,
    organizationError: neighborhoodsError,
  } = useResidentReportFilterOptions(user?.organization_id ?? "", { loadUsers: false });
  useEffect(() => {
    if (!neighborhoodOptions.length) return;
    if (!scope || !neighborhoodOptions.some((neighborhood) => neighborhood.id === scope)) {
      setScope(neighborhoodOptions[0].id);
    }
  }, [neighborhoodOptions, scope]);

  const accessClassName = (access: ReportTemplate["access"]) =>
    access === "aggregate" ? "bg-[#d4f5ea] text-[#006b58]" : "bg-[#fff0cc] text-[#805200]";

  return (
    <main
      className="min-h-full bg-[#effbf3] px-4 py-6 text-[#004b3a] sm:px-6 lg:px-8"
      aria-labelledby="reports-presentation-title"
    >
      <div className="mx-auto max-w-[1440px] space-y-6">
        <header className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#008a72]">
              {t("reports_presentation.workspace")}
            </p>
            <h1
              id="reports-presentation-title"
              className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl"
            >
              {t("reports_presentation.title")}
            </h1>
            <p className="mt-2 max-w-4xl text-sm font-medium text-[#587268] sm:text-base">
              {t("reports_presentation.description")}
            </p>
          </div>
        </header>

        <div className="grid items-start gap-6 xl:grid-cols-[330px_minmax(0,1fr)]">
          <section aria-labelledby="report-templates-title">
            <p
              id="report-templates-title"
              className="mb-3 text-xs font-bold uppercase tracking-[0.1em] text-[#587268]"
            >
              {t("reports_presentation.choose_template")}
            </p>
            <div className="space-y-3">
              {reportTemplates.map((template) => {
                const isSelected = selectedTemplateId === template.id;
                const selectionClassName = isSelected
                  ? "border-[#00a98f] ring-2 ring-[#a8eadb]"
                  : "border-[#dbe9e1] hover:border-[#77cdbb]";

                return (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => setSelectedTemplateId(template.id)}
                    className={
                      "w-full rounded-2xl border bg-white p-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00c49a] " +
                      selectionClassName
                    }
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h2 className="text-base font-bold text-[#004b3a]">{template.title}</h2>
                      <span
                        className={
                          "shrink-0 rounded-full px-2.5 py-1 text-xs font-bold " +
                          accessClassName(template.access)
                        }
                      >
                        {t("reports_presentation.access." + template.access)}
                      </span>
                    </div>
                    <p className="mt-2 text-sm leading-5 text-[#587268]">{template.description}</p>
                    <p className="mt-2 text-sm font-semibold text-[#315f53]">
                      {t("reports_presentation.for") + " " + template.audience}
                    </p>
                  </button>
                );
              })}
            </div>
          </section>

          <Card className="rounded-3xl border border-[#dbe9e1] bg-white p-5 shadow-sm sm:p-7">
            <div>
              <section aria-labelledby="report-setup-title">
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#587268]">
                  {t("reports_presentation.setup_step")}
                </p>
                <h2
                  id="report-setup-title"
                  className="mt-3 text-2xl font-bold"
                >
                  {selectedTemplate.title}
                </h2>
                <div className="mt-6 space-y-4">
                  <SelectV2
                    value={scope}
                    onChange={setScope}
                    label={t("reports_presentation.scope")}
                    ariaLabel={t("reports_presentation.scope")}
                    placeholder={
                      isLoadingNeighborhoods
                        ? t("reports_presentation.loading_neighborhoods")
                        : t("reports_presentation.select_neighborhood")
                    }
                    options={neighborhoodOptions.map((neighborhood) => ({
                      value: neighborhood.id,
                      label: neighborhood.name,
                    }))}
                    disabled={
                      isLoadingNeighborhoods ||
                      Boolean(neighborhoodsError) ||
                      !neighborhoodOptions.length
                    }
                  />
                  {neighborhoodsError ? (
                    <p className="text-sm text-destructive">
                      {t("reports_presentation.neighborhoods_unavailable")}
                    </p>
                  ) : null}
                  {!isLoadingNeighborhoods && !neighborhoodsError && !neighborhoodOptions.length ? (
                    <p className="text-sm text-[#587268]">
                      {t("reports_presentation.no_neighborhoods")}
                    </p>
                  ) : null}
                  <SelectV2
                    value={period}
                    onChange={setPeriod}
                    label={t("reports_presentation.time_period")}
                    ariaLabel={t("reports_presentation.time_period")}
                    options={[
                      {
                        value: "last_quarter",
                        label: t("reports_presentation.periods.last_quarter"),
                      },
                      {
                        value: "last_12_months",
                        label: t("reports_presentation.periods.last_12_months"),
                      },
                      {
                        value: "year_to_date",
                        label: t("reports_presentation.periods.year_to_date"),
                      },
                    ]}
                  />
                  <SelectV2
                    value={comparison}
                    onChange={setComparison}
                    label={t("reports_presentation.compare_against")}
                    ariaLabel={t("reports_presentation.compare_against")}
                    options={[
                      {
                        value: "last_year",
                        label: t("reports_presentation.comparisons.last_year"),
                      },
                      {
                        value: "previous_period",
                        label: t("reports_presentation.comparisons.previous_period"),
                      },
                      { value: "none", label: t("reports_presentation.comparisons.none") },
                    ]}
                  />
                  <div>
                    <p className="text-sm font-semibold text-[#004b3a]">
                      {t("reports_presentation.format")}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(["pdf", "powerpoint", "csv"] as ReportFormat[]).map((option) => (
                        <Button
                          key={option}
                          type="button"
                          variant={format === option ? "brand-secondary" : "brand-outline"}
                          onClick={() => setFormat(option)}
                          className="rounded-full px-4"
                        >
                          {option === "powerpoint" ? "PowerPoint" : option.toUpperCase()}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <SelectV2
                    value={cadence}
                    onChange={setCadence}
                    label={t("reports_presentation.send_automatically")}
                    ariaLabel={t("reports_presentation.send_automatically")}
                    options={[
                      { value: "monthly", label: t("reports_presentation.cadences.monthly") },
                      { value: "quarterly", label: t("reports_presentation.cadences.quarterly") },
                      { value: "manual", label: t("reports_presentation.cadences.manual") },
                    ]}
                  />
                  <div className="space-y-1.5">
                    <label
                      htmlFor="report-presentation-recipients"
                      className="text-sm font-semibold text-[#004b3a]"
                    >
                      {t("reports_presentation.recipients")}
                    </label>
                    <Input
                      id="report-presentation-recipients"
                      value={recipients}
                      onChange={(event) => setRecipients(event.target.value)}
                      placeholder={t("reports_presentation.recipients_placeholder")}
                      startIcon={<Mail className="h-4 w-4 text-[#587268]" />}
                    />
                  </div>
                </div>
                <div className="mt-5 rounded-xl bg-[#d4f5ea] p-4 text-sm text-[#005342]">
                  <p className="font-bold">{t("reports_presentation.aggregate_only")}</p>
                  <p className="mt-1">{t("reports_presentation.aggregate_only_description")}</p>
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Button
                    type="button"
                    variant="primary"
                    className="rounded-full px-5"
                    onClick={() => navigate(selectedTemplate.route)}
                  >
                    <ExternalLink />
                    {t("reports_presentation.view_report")}
                  </Button>
                </div>
              </section>
            </div>
          </Card>
        </div>
      </div>
    </main>
  );
}
